import AppBar from '@mui/material/AppBar';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { ConnectionStatus } from '@/components/ConnectionStatus';
import { WSIndicator } from '@/components/ConnectionStatus/WSIndicator';
import { getEnvBorder } from '@/contexts/theme/utils';
import { LightDivider } from '@/components/common/LightDivider';
import { EnvironmentInfo } from '@/components/Navbar/EnvironmentInfo';
import { ClosedMarketInfo } from '@/components/Navbar/ClosedMarketInfo';
import { RankInfo } from '@/components/Navbar/RankInfo';
import { MarketButton } from '@/components/Market/MarketButton';
import { Market } from '@/components/Market';
import { ScoringContext } from '@/contexts/scoring/context';
import { COMMON_STRING } from '@/constants/dictionary';
import { ROUTE_PATHS } from '@/router/constants';
import { AppVersion } from '../AppVersion';
import { PermissionsChecker } from '../PermissionsChecker/PermissionsChecker';
import { NavBarBadge } from '../common/NavBarBadge';
import { ArchiveGuard } from '../common/ArchiveGuard';
import { NetworkStatus } from './NetworkStatus';
import { UserMenu } from './UserMenu';
import { FixtureNavbarItems } from './FixtureNavbarItems';
import { MarketStatus } from './MarketStatus';

export const FIXTURE_INFO_BUTTON_LABEL = 'Open Fixture Info';
export const DATA_COLLECTORS_BUTTON_LABEL = 'Open Data Collectors';
export const SCOREBORD_BUTTON_LABEL = 'Open Scoreboard';
export const SERVER_INDICATOR_TEST_ID = 'server-indicator';
export const SETTING_BUTTON_LABEL = 'user-menu-button';

export const Navbar = () => {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down('sm'));
  const { pathname } = useLocation();

  const {
    state: { fixtureSummary },
  } = useContext(ScoringContext);

  return (
    <Stack>
      <ClosedMarketInfo />
      <AppBar
        sx={{
          borderTop: getEnvBorder(theme),
        }}
        position='sticky'
      >
        <Toolbar>
          <Stack direction={'row'} flexGrow={1} alignItems='center' gap={1}>
            <Stack gap={0.5}>
              <Typography fontWeight={700} component='span' fontSize='1em'>
                {isXS ? 'MT' : 'Monitoring Tool'}
              </Typography>
              <Stack flexDirection='row' gap={0.5}>
                <AppVersion />
              </Stack>
            </Stack>
            <RankInfo />
            <EnvironmentInfo />
            {fixtureSummary?.isArchive && pathname !== ROUTE_PATHS.HOME && (
              <NavBarBadge
                label={COMMON_STRING.ARCHIVED}
                sxProps={{ bgcolor: theme.palette.error.main }}
              />
            )}
          </Stack>
          <Stack direction={'row'} gap={0.5} alignItems='center'>
            <PermissionsChecker name='FixturePage.HeaderPanel'>
              <Stack direction={'row'} gap={0.5} alignItems='center'>
                <PermissionsChecker name='FixturePage.HeaderPanel.MarketChange'>
                  <MarketButton />
                  <ArchiveGuard hideMode>
                    <Market />
                  </ArchiveGuard>
                  <MarketStatus />
                </PermissionsChecker>
                <PermissionsChecker name='FixturePage.HeaderPanel.ServerAvailability'>
                  <NetworkStatus />
                  <LightDivider orientation='vertical' flexItem />
                  <ConnectionStatus
                    label={'Server'}
                    customIndicator={<WSIndicator />}
                    data-testid={SERVER_INDICATOR_TEST_ID}
                  />
                </PermissionsChecker>
                <LightDivider orientation='vertical' flexItem />
                <FixtureNavbarItems />
              </Stack>
            </PermissionsChecker>
            <UserMenu />
          </Stack>
        </Toolbar>
      </AppBar>
    </Stack>
  );
};
